import { useMemo } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { FinancialSummaryRow } from '../FinancialSummaryRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import type {
  PotFinancials,
  FixedRateWorker,
} from '../../types/potDetails.types';

interface PotFinancialBreakdownCardProps {
  financials: PotFinancials;
  fixedRateWorkers: FixedRateWorker[];
}

export function PotFinancialBreakdownCard({
  financials,
  fixedRateWorkers,
}: PotFinancialBreakdownCardProps) {
  const { fixedRateTotal, adHocTotal } = useMemo(() => {
    const gangMembers = fixedRateWorkers.filter(w => w.isGangMember);
    const adhoc = fixedRateWorkers.filter(w => !w.isGangMember);
    return {
      fixedRateTotal: gangMembers.reduce((sum, w) => sum + w.totalAmount, 0),
      adHocTotal: adhoc.reduce((sum, w) => sum + w.totalAmount, 0),
    };
  }, [fixedRateWorkers]);

  return (
    <Card
      variant="outlined"
      data-testid="pot-financial-breakdown-card"
      sx={{ height: '100%' }}
    >
      <CardContent
        sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Financial Summary
        </Typography>
        <Stack spacing={2}>
          <FinancialSummaryRow
            label="Gross Amount"
            value={formatCurrency(financials.grossAmount)}
          />
          {fixedRateTotal > 0 && (
            <FinancialSummaryRow
              label="Fixed Rate Workers"
              value={`-${formatCurrency(fixedRateTotal)}`}
              valueColor="error"
            />
          )}
          {adHocTotal > 0 && (
            <FinancialSummaryRow
              label="Ad-hoc Workers"
              value={`-${formatCurrency(adHocTotal)}`}
              valueColor="error"
            />
          )}
          {financials.totalDeductions > 0 && (
            <FinancialSummaryRow
              label="Deductions"
              value={`-${formatCurrency(financials.totalDeductions)}`}
              valueColor="error"
            />
          )}
          <FinancialSummaryRow
            label="Net Amount"
            value={formatCurrency(financials.netAmount)}
            valueColor="primary"
            emphasis
            dividerAbove
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
